
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import { useVuelidate, Validation } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { VueRecaptcha } from 'vue-recaptcha'
import { RoundedButton } from '@mg/micro/vue2'
import AlertBox from '@mg/web-components/Atoms/AlertBox'
import { TranslateResult } from 'vue-i18n'
import FieldInputRow from '~/components/account/FieldInputRow.vue'
import { ApiValidationErrors } from '~/utils/apolloErrors'
import {
  PersonalDataFormType,
  PersonalDataFormSubmitData,
  PersonalDataFormFieldsTranslations,
  PersonalDataFormFieldsValidations,
} from './types'

@Component({
  components: {
    FieldInputRow,
    RoundedButton,
    VueRecaptcha,
    AlertBox,
  },
  setup() {
    return {
      v$: useVuelidate(),
    }
  },
  validations(): PersonalDataFormFieldsValidations {
    const validations = {
      firstname: {
        required,
      },
      lastname: {
        required,
      },
      email: {
        required,
        email,
      },
      requestText: {},
      recaptchaToken: {},
    }
    return validations as PersonalDataFormFieldsValidations
  },
})
export default class PersonalDataFormFields extends Vue {
  @Prop({ type: Object, required: true })
  formType!: PersonalDataFormType

  @Prop({ type: Object, required: true })
  translations!: PersonalDataFormFieldsTranslations

  @Prop({ default: false, type: Boolean })
  isLoading!: boolean

  @Prop({ type: Object, default: () => ({}) })
  readonly apiErrors!: ApiValidationErrors

  @Prop(Object) readonly prefill?: {
    firstname: string
    lastname: string
    email: string
  } | null

  firstname: string = ''
  lastname: string = ''
  email: string = ''
  requestText: string = ''
  recaptchaToken: string = ''

  // recaptcha is disabled it makes trouble with SSR - .createElement() called outside of render function
  useRecaptcha: boolean = true

  mounted() {
    if (this.prefill) {
      this.firstname = this.prefill.firstname || ''
      this.lastname = this.prefill.lastname || ''
      this.email = this.prefill.email || ''
    }
    this.validator.$reset()
  }

  get recaptchaSiteKey(): string {
    if (!this.$shopConfig.webConfig?.recaptchaSiteKey2026) {
      this.$logger.error(
        'PersonalDataFormFields webConfig.recaptchaSiteKey2026 missing'
      )
    }
    return this.$shopConfig.webConfig?.recaptchaSiteKey2026
  }

  get emailErrorMessage(): TranslateResult {
    const field = this.validator.email as any
    if (!field.$invalid) {
      return ''
    }
    if (field.required?.$invalid) {
      return this.translations.validationRequired
    }
    if (field.email?.$invalid) {
      return this.translations.validationEmail
    }
    return this.translations.validationRequired
  }

  submitWithValidation() {
    this.validateForm()
  }

  submitWithRecaptcha() {
    if (this.isLoading) {
      // prevent double submit
      return
    }
    const recaptcha = this.$refs.recaptcha as any
    if (recaptcha?.execute) {
      recaptcha.execute()
    }
  }

  onCaptchaVerified(token: string) {
    this.recaptchaToken = token
    this.handleFormSubmit()
    this.onCaptchaExpired()
  }

  onCaptchaExpired() {
    const recaptcha = this.$refs.recaptcha as any
    if (recaptcha?.reset) {
      recaptcha.reset()
    }
    this.recaptchaToken = ''
  }

  onCaptchaError() {
    this.$logger.error('Google recaptcha error')
  }

  validateForm() {
    this.validator.$touch()
    if (!this.validator.$invalid) {
      if (this.useRecaptcha) {
        this.submitWithRecaptcha()
      } else {
        this.handleFormSubmit()
      }
    }
  }

  handleFormSubmit() {
    const data: PersonalDataFormSubmitData = {
      firstname: this.firstname,
      lastname: this.lastname,
      email: this.email,
      requestText: this.requestText,
      recaptchaToken: this.recaptchaToken,
      typeId: parseInt(this.formType.id, 10),
    }
    this.$emit('submit', data)
  }

  @Emit()
  changed(field?: string) {
    return field
  }

  get validator(): Validation {
    return (this as any).v$ as Validation
  }
}
